import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, setStoredToken } from "@/api";
import { useAuth } from "@/context/AuthContext";

export default function ResetPasswordPage() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { refresh } = useAuth();
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (password.length < 6) {
            setError("Password must be at least 6 characters.");
            return;
        }
        if (password !== confirm) {
            setError("Passwords don't match.");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/auth/reset-password", { token, password });
            // Persist the returned JWT for WebViews that drop cookies.
            if (data?.access_token) setStoredToken(data.access_token);
            await refresh();
            toast.success(`Welcome back, ${data.name.split(" ")[0]}! Password updated.`);
            navigate(data.role === "manager" ? "/manager" : "/operative", { replace: true });
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-6 py-10 bg-[var(--bg)]">
            <div className="w-full max-w-md animate-in-up">
                <div className="flex items-center gap-3 mb-8">
                    <img src="/scs-icon-512.png" alt="SCS" className="h-12 w-12 rounded-2xl object-cover" />
                    <div className="leading-tight">
                        <div className="font-display text-lg font-semibold">SCS</div>
                        <div className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[var(--terracotta)]">
                            Simple · Clear · Systems
                        </div>
                    </div>
                </div>

                <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight" data-testid="reset-heading">
                    Choose a new password
                </h1>
                <p className="mt-2 text-[var(--text-secondary)] text-sm">
                    Pick something you'll remember — at least 6 characters. We'll sign you straight in once you save.
                </p>

                <form onSubmit={submit} className="mt-6 space-y-4">
                    <div>
                        <label className="text-xs uppercase tracking-[0.18em] text-[var(--text-muted)] font-medium">New password</label>
                        <div className="relative mt-1">
                            <input data-testid="reset-password-input"
                                   type={show ? "text" : "password"}
                                   className="input-field pr-11"
                                   value={password}
                                   onChange={e => { setPassword(e.target.value); setError(""); }}
                                   autoComplete="new-password"
                                   required />
                            <button type="button"
                                    data-testid="reset-toggle-password-btn"
                                    onClick={() => setShow(v => !v)}
                                    aria-label={show ? "Hide password" : "Show password"}
                                    className="absolute inset-y-0 right-2 flex items-center justify-center h-full w-8 text-[var(--text-muted)] hover:text-[var(--pine)] transition-colors">
                                {show ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="text-xs uppercase tracking-[0.18em] text-[var(--text-muted)] font-medium">Confirm new password</label>
                        <input data-testid="reset-confirm-input"
                               type={show ? "text" : "password"}
                               className="input-field mt-1"
                               value={confirm}
                               onChange={e => { setConfirm(e.target.value); setError(""); }}
                               autoComplete="new-password"
                               required />
                    </div>

                    {error && (
                        <div data-testid="reset-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {error}
                        </div>
                    )}

                    <button data-testid="reset-submit-btn" type="submit" disabled={busy}
                            className="btn-primary w-full">
                        {busy ? "Saving…" : "Save & sign in"}
                    </button>
                </form>

                <div className="mt-6 text-sm">
                    <Link data-testid="reset-back-link" to="/login"
                          className="text-[var(--pine)] font-medium hover:underline inline-flex items-center gap-1">
                        <ArrowLeft size={14} /> Back to sign in
                    </Link>
                </div>
            </div>
        </div>
    );
}
